import { useState, useEffect } from 'react';
import './UserAvatar.css';
import { usersAPI, picturesAPI } from './services/api';
import { AvatarType } from './constants/avatarTypes';

function UserAvatar({ user, size = 'medium', showName = false }) {
  const [lastMonthCount, setLastMonthCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLastMonthCount = async () => {
      if (user && user.communityWorkCount > 0) {
        try {
          setLoading(true);
          const count = await usersAPI.getCommunityWorkLastMonth(user.id);
          setLastMonthCount(count);
        } catch (error) {
          console.error('Error loading community work count:', error);
          setLastMonthCount(0);
        } finally {
          setLoading(false);
        }
      } else {
        setLastMonthCount(0);
        setLoading(false);
      }
    };

    loadLastMonthCount();
  }, [user]);

  if (!user) return null;

  const avatarType = user.avatarType ?? AvatarType.PictureAndName;

  // Get color based on last month activity with different base colors
  const getAvatarColor = (count, baseColorRgb) => {
    if (count === 0) return { bg: '#e0e0e0', text: '#666' }; // Grey for no activity

    // Parse RGB values
    const [r, g, b] = baseColorRgb;

    // Calculate darkness based on monthly count (1-10+)
    const darkenFactor = Math.min(count / 10, 1); // 0 to 1
    const lightness = 1 - (darkenFactor * 0.5); // 1.0 to 0.5 (lighter to darker)

    const adjustedR = Math.round(r * lightness);
    const adjustedG = Math.round(g * lightness);
    const adjustedB = Math.round(b * lightness);

    return {
      bg: `rgb(${adjustedR}, ${adjustedG}, ${adjustedB})`,
      text: darkenFactor > 0.4 ? '#ffffff' : '#333333'
    };
  };

  // Community Work Avatar (Green)
  if (avatarType === AvatarType.CommunityWork && user.communityWorkCount > 0) {
    const colors = getAvatarColor(lastMonthCount, [45, 125, 74]); // Green base
    const starCount = user.totalStars || 0;
    return (
      <div className={`user-avatar activity-avatar ${size}`}>
        <div className="avatar-circle-wrapper">
          <div
            className="activity-circle"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {loading ? '...' : user.communityWorkCount}
          </div>
          {starCount > 0 && (
            <div className="star-badge">⭐ {starCount}</div>
          )}
        </div>
        {showName && (
          <div className="avatar-name">{user.displayName}</div>
        )}
      </div>
    );
  }

  // TODO: Future avatar types
  // // Public Activity Art Avatar (Blue)
  // if (avatarType === 'public-art' && user.publicActivityArtCount > 0) {
  //   const monthCount = user.publicActivityArtLastMonth || 0;
  //   const colors = getAvatarColor(monthCount, [33, 150, 243]); // Blue base (matching public activity button)
  //   const starCount = user.totalStars || 0;
  //   return (
  //     <div className={`user-avatar activity-avatar ${size}`}>
  //       <div className="avatar-circle-wrapper">
  //         <div
  //           className="activity-circle"
  //           style={{ backgroundColor: colors.bg, color: colors.text }}
  //         >
  //           {user.publicActivityArtCount}
  //         </div>
  //         {starCount > 0 && (
  //           <div className="star-badge">⭐ {starCount}</div>
  //         )}
  //       </div>
  //       {showName && (
  //         <div className="avatar-name">{user.displayName}</div>
  //       )}
  //     </div>
  //   );
  // }

  // // Public Activity Sport Avatar (Orange)
  // if (avatarType === 'public-sport' && user.publicActivitySportCount > 0) {
  //   const monthCount = user.publicActivitySportLastMonth || 0;
  //   const colors = getAvatarColor(monthCount, [255, 152, 0]); // Orange base
  //   const starCount = user.totalStars || 0;
  //   return (
  //     <div className={`user-avatar activity-avatar ${size}`}>
  //       <div className="avatar-circle-wrapper">
  //         <div
  //           className="activity-circle"
  //           style={{ backgroundColor: colors.bg, color: colors.text }}
  //         >
  //           {user.publicActivitySportCount}
  //         </div>
  //         {starCount > 0 && (
  //           <div className="star-badge">⭐ {starCount}</div>
  //         )}
  //       </div>
  //       {showName && (
  //         <div className="avatar-name">{user.displayName}</div>
  //       )}
  //     </div>
  //   );
  // }

  // // Public Activity Education Avatar (Purple)
  // if (avatarType === 'public-education' && user.publicActivityEducationCount > 0) {
  //   const monthCount = user.publicActivityEducationLastMonth || 0;
  //   const colors = getAvatarColor(monthCount, [156, 39, 176]); // Purple base
  //   const starCount = user.totalStars || 0;
  //   return (
  //     <div className={`user-avatar activity-avatar ${size}`}>
  //       <div className="avatar-circle-wrapper">
  //         <div
  //           className="activity-circle"
  //           style={{ backgroundColor: colors.bg, color: colors.text }}
  //         >
  //           {user.publicActivityEducationCount}
  //         </div>
  //         {starCount > 0 && (
  //           <div className="star-badge">⭐ {starCount}</div>
  //         )}
  //       </div>
  //       {showName && (
  //         <div className="avatar-name">{user.displayName}</div>
  //       )}
  //     </div>
  //   );
  // }

  // Default Avatar (Picture/Name)
  const starCount = user.totalStars || 0;

  // If user has selected an avatar picture, show it
  if (user.avatarPictureId && user.avatarPictureSysName) {
    const imageUrl = picturesAPI.getImage(user.avatarPictureSysName, user.username);
    return (
      <div className={`user-avatar default-avatar ${size}`}>
        <div className="avatar-circle-wrapper">
          <img
            src={imageUrl}
            alt={user.displayName}
            className="avatar-picture-img"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="avatar-picture" style={{ display: 'none' }}>
            {user.displayName.charAt(0).toUpperCase()}
          </div>
          {starCount > 0 && (
            <div className="star-badge">⭐ {starCount}</div>
          )}
        </div>
        {showName && (
          <div className="avatar-name">{user.displayName}</div>
        )}
      </div>
    );
  }

  // Otherwise show initials
  return (
    <div className={`user-avatar default-avatar ${size}`}>
      <div className="avatar-circle-wrapper">
        <div className="avatar-picture">
          {user.displayName.charAt(0).toUpperCase()}
        </div>
        {starCount > 0 && (
          <div className="star-badge">⭐ {starCount}</div>
        )}
      </div>
      {showName && (
        <div className="avatar-name">{user.displayName}</div>
      )}
    </div>
  );
}

export default UserAvatar;
